import {
  Button,
  Classes,
  Dialog,
  FileInput,
  FormGroup,
  H5,
  InputGroup,
  Popover,
  Switch,
  Tab,
  Tabs,
} from "@blueprintjs/core";
import { observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { IMva } from "../../model";
import { AppStore } from "../../stores/AppStore";
import { MvaStorePA } from "../../stores/mvas/MvaStorePA";
import { Pager } from "../utils/Pager";
import { MvaPaDialogCreate } from "./MvaPaDialogCreate";
import * as moment from "moment";
import {
  MDBBtn,
  MDBCol,
  MDBRow,
} from "mdbreact";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import { MvaPaDialogEdit } from "./MvaPaDialogEdit";
import { MvaPaDialogDuplicar } from "./MvaPaDialogDuplicar";

var debounce = require('lodash.debounce');

interface State {
  filter: string
}

@observer
export class MvasPaPage extends React.Component<{}, State> {

  obs = observable({
    adding: false as boolean,
    edit_mva: null as IMva,
    duplicar_mva: null as IMva,
    loading_export: false as boolean,
    fetchOptions: {} as any,
    loading_import: false as boolean,
    open_import_produtos: false as boolean,
    file_name: "Selecione um arquivo em xlsx igual ao exportado!" as string,
  })

  constructor(props) {
    super(props)

    this.state = {
      filter: ""
    }

    this.obs.fetchOptions = debounce(this.handlePageFilter.bind(this), 800);

  }
  componentDidMount() {
    AppStore.setTitle("MVAs Pará");
    MvaStorePA.handlePageFilter(1, "")
  }

  /**
  * Ativa o modo de adição de novo registro de MVA.
  *
  * A função:
  * - Define `obs.adding` como `true`, indicando que o usuário está iniciando o processo de adição.
  *
  * @returns {void} Não retorna valor.
  */

  handleAdd = () => {
    this.obs.adding = true;
  };

  /**
  * Fecha o modal de inclusão (Add) e recarrega os dados da página atual com o filtro aplicado.
  *
  * A função:
  * - Reseta a flag `this.obs.adding` para `null`, indicando que não há inclusão em andamento.
  * - Chama `MvaStorePA.handlePageFilter` para atualizar a lista de registros de acordo com a
  *   página atual (`MvaStorePA.obs.currentPage`) e o filtro ativo (`this.state.filter`).
  *
  * @async
  * @returns {Promise<void>} Promise resolvida após a atualização da listagem.
  */

  handleAddClose = async () => {
    this.obs.adding = null;

    await MvaStorePA.handlePageFilter(MvaStorePA.obs.currentPage, this.state.filter);

  };

  /**
  * Fecha o modal de edição e recarrega os dados da página atual com o filtro aplicado.
  *
  * A função:
  * - Reseta a flag `this.obs.edit_mva` para `null`, indicando que não há edição em andamento.
  * - Reaplica o filtro da página via `MvaStorePA.handlePageFilter`.
  *
  * @async
  * @returns {Promise<void>} Promise resolvida após a atualização da listagem.
  */

  handleEditClose = async () => {
    this.obs.edit_mva = null;

    await MvaStorePA.handlePageFilter(MvaStorePA.obs.currentPage, this.state.filter);

  };

  /**
  * Fecha o modal de duplicação e recarrega os dados da página atual com o filtro aplicado.
  *
  * A função:
  * - Reseta a flag `this.obs.duplicar_mva` para `null`, indicando que não há duplicação em andamento.
  * - Reaplica o filtro da página via `MvaStorePA.handlePageFilter`.
  *
  * @async
  * @returns {Promise<void>} Promise resolvida após a atualização da listagem.
  */

  handleDuplicaClose = async () => {
    this.obs.duplicar_mva = null;

    await MvaStorePA.handlePageFilter(MvaStorePA.obs.currentPage, this.state.filter);

  };

  /**
  * Recarrega a listagem de registros ao mudar de página.
  *
  * A função:
  * - É chamada quando o usuário seleciona uma nova página de resultados.
  * - Invoca `MvaStorePA.handlePageFilter` passando a página atual e o filtro em uso.
  *
  * @param {number} page Número da página selecionada.
  * @returns {void}
  */

  handlePageChange = (page: number) => {
    MvaStorePA.handlePageFilter(MvaStorePA.obs.currentPage, this.state.filter);
  };

  /**
  * Formata um valor de NCM (ou múltiplos NCMs) em elementos HTML.
  *
  * A função:
  * - Divide a string de NCMs por quebras de linha (`\n`).
  * - Para cada valor obtido, gera um elemento `<p>` exibindo o NCM.
  * - Retorna todos os parágrafos agrupados dentro de uma `<div>`.
  *
  * ## Comportamento
  * - Se houver apenas um NCM, retorna uma `<div>` com um único `<p>`.
  * - Se houver múltiplos NCMs separados por `\n`, cada um é exibido em sua própria linha.
  *
  * @param {string} ncm Texto contendo um ou mais códigos NCM, separados por `\n`.
  *
  * @returns {JSX.Element} Um elemento `<div>` contendo os NCMs renderizados em `<p>`.
  *
  */

  ncmFormatter(ncm) {
    const ncmHTML = ncm.split("\n")?.map((ncm, index) => {
      return <p>{ncm}</p>;
    });
    return (
      <div>
        {ncmHTML}
      </div>
    );
  }

  /**
  * Formata uma data de validade para o padrão brasileiro `DD/MM/YYYY`.
  *
  * A função:
  * - Recebe uma data (`d`) em qualquer formato aceito pelo **moment.js**.
  * - Adiciona 3 horas ao valor original (ajuste de fuso/servidor).
  * - Retorna a data formatada no padrão `DD/MM/YYYY`.
  * - Caso `d` seja `null` ou `undefined`, retorna `null`.
  *
  * @param {string|Date|number|null|undefined} d Data a ser formatada.
  * Aceita string em formato ISO, objeto `Date` ou timestamp numérico.
  *
  * @returns {string|null} Data formatada como `DD/MM/YYYY`, ou `null` se não houver data.
  */

  validadeFormatter(d) {
    return d ? moment(d).add(3, "hour").format("DD/MM/YYYY") : null;
  }

  /**
  * Alterna o filtro de vigência e recarrega a listagem de MVAs.
  *
  * A função:
  * - Inverte o valor booleano de `MvaStorePA.obs.filter_vigencia`.
  * - Chama `MvaStorePA.handlePageFilter`, passando:
  *   - A página atual (`MvaStorePA.obs.currentPage`).
  *   - O filtro atual (`this.state.filter`).
  *
  * @returns {void} Não retorna valor.
  */

  handleChangeVigencia() {
    MvaStorePA.obs.filter_vigencia = !MvaStorePA.obs.filter_vigencia;
    MvaStorePA.handlePageFilter(MvaStorePA.obs.currentPage, this.state.filter);
  }

  /**
  * Aplica o filtro atual e reinicia a listagem de MVAs na página 1.
  *
  * A função:
  * - Atualiza `MvaStorePA.obs.currentPage` para 1.
  * - Chama `MvaStorePA.handlePageFilter`, passando:
  *   - Página 1.
  *   - O valor atual do filtro (`this.state.filter`).
  *
  * @async
  * @returns {Promise<void>} Promise resolvida após a aplicação do filtro e recarregamento da listagem.
  */

  async handlePageFilter() {
    MvaStorePA.obs.currentPage = 1
    await MvaStorePA.handlePageFilter(1, this.state.filter)
  }

  /**
  * Exporta os dados de MVA no contexto do PA com base na aba selecionada.
  *
  * A função:
  * - Ativa o estado de carregamento `obs.loading_export`.
  * - Chama `MvaStorePA.handleExport`, passando:
  *   - `"pa"` como estado.
  *   - `true` se a aba selecionada for `"entrada"`, `false` caso contrário.
  * - Após a exportação, desativa o estado de carregamento.
  *
  * @async
  * @returns {Promise<void>} Promise resolvida após a tentativa de exportação.
  */

  async handleExport() {
    this.obs.loading_export = true

    await MvaStorePA.handleExport("pa",MvaStorePA.obs.selectedTab == "entrada")

    this.obs.loading_export = false
  }

  /**
  * Altera a aba selecionada e recarrega a listagem de MVAs com filtros resetados.
  *
  * A função:
  * - Atualiza `MvaStorePA.obs.selectedTab` com o ID da nova aba.
  * - Ativa o filtro de vigência via `MvaStorePA.obs.filter_vigencia = true`.
  * - Reseta o filtro local (`this.state.filter`) para uma string vazia.
  * - Executa `handlePageFilter` com a página 1 e filtro vazio, recarregando os dados.
  *
  * @async
  * @param {string} tabId ID da aba selecionada (ex: "entrada", "saida").
  *
  * @returns {Promise<void>} Promise resolvida após a atualização da aba e recarregamento dos dados.
  */

  async handleChangeTab(tabId: string) {
    MvaStorePA.obs.selectedTab = tabId

    MvaStorePA.obs.filter_vigencia = true
    this.setState({ filter: "" })

    await MvaStorePA.handlePageFilter(1, "")
  }

  /**
 * Atualiza o valor do filtro de busca e recarrega as opções disponíveis.
 *
 * A função:
 * - Atualiza o estado local `filter` com o valor fornecido.
 * - Após a atualização do estado, executa `obs.fetchOptions()` para buscar novas opções baseadas no filtro atualizado.
 *
 * @param {string} e Valor do filtro digitado pelo usuário.
 *
 * @returns {void} Não retorna valor.
 */

  handleChangeFilter(e) {

    this.setState({ filter: e }, () => {

      this.obs.fetchOptions()

    })

  }

  /**
  * Exclui um registro de MVA no contexto do PA via admin.
  *
  * A função:
  * - Chama `MvaStorePA.handleDeleteMva(id)` para realizar a exclusão do MVA com o ID informado.
  * - Se a exclusão for bem-sucedida (res === true):
  *   - Reexecuta o filtro de paginação atual via `MvaStorePA.handlePageFilter`, mantendo a página atual e os filtros existentes.
  *
  * @async
  * @param {number|string} id ID do MVA a ser excluído.
  *
  * @returns {Promise<void>} Promise resolvida após a tentativa de exclusão e atualização da listagem.
  */

  async handleDeleteMva(id) {
    let res = await MvaStorePA.handleDeleteMva(id)

    if (res) {
      await MvaStorePA.handlePageFilter(MvaStorePA.obs.currentPage, this.state.filter);
    }

  }

  /**
  * Renderiza o campo de busca customizado para filtrar a listagem de MVAs.
  *
  * A função:
  * - Retorna um layout contendo:
  *   - Um `InputGroup` com placeholder para filtro por Item, CEST ou Descrição.
  *   - Um botão "Buscar" que dispara o filtro ao ser clicado.
  * - O valor do campo de busca está vinculado ao estado local `this.state.filter`.
  * - Atualiza o filtro via `handleChangeFilter` a cada alteração no input.
  * - Ao clicar no botão, executa `handlePageFilter()` para aplicar o filtro.
  *
  * @param {Object} props Propriedades React passadas ao componente (não utilizadas diretamente aqui).
  *
  * @returns {JSX.Element} JSX com layout responsivo do campo de filtro e botão de busca.
  */

  createCustomSearchField = (props) => {
    return (
      <MDBRow>
        <MDBCol xl="10" lg="10" md="10" sm="10">
          <InputGroup
            round
            style={{ borderRadius: "10px" }}
            placeholder="Filtrar (Item, CEST, Descrição)"
            value={this.state.filter}
            onChange={(e) => this.handleChangeFilter(e.target.value)}
          />
        </MDBCol>

        <MDBCol md="2">
          <MDBBtn
            className="btnDefault btnBuscarNfe"
            style={{ marginTop: 4 }}
            onClick={() => this.handlePageFilter()}
          >
            Buscar
          </MDBBtn>
        </MDBCol>
      </MDBRow>
    );
  };

  /**
  * Abre o modal de importação de produtos MVA no contexto do PA.
  *
  * A função:
  * - Define `obs.open_import_produtos` como `true`, exibindo o modal de importação.
  *
  * @returns {void} Não retorna valor.
  */

  handleImport() {

    this.obs.open_import_produtos = true;

  };

  /**
  * Lida com a importação de um arquivo XLSX de MVA no contexto do PA via admin.
  *
  * A função:
  * - Ativa o estado de carregamento `obs.loading_import`.
  * - Captura o arquivo selecionado no input.
  * - Atualiza o nome do arquivo em `obs.file_name`.
  * - Cria um `FormData` contendo:
  *   - O arquivo selecionado.
  *   - A flag `entrada` baseada na aba selecionada (`MvaStorePA.obs.selectedTab`).
  *   - O estado fixo `"pa"`.
  * - Fecha o modal de importação (`obs.open_import_produtos = false`).
  * - Envia os dados para `MvaStorePA.handleImport(data)` para processar a importação.
  * - Se a resposta for positiva:
  *   - Reinicia a paginação para a página 1.
  *   - Aplica filtro vazio via `handlePageFilter`.
  *   - Restaura o texto do seletor de arquivo.
  * - Desativa o estado de carregamento `obs.loading_import`.
  *
  * @async
  * @param {Event} event Evento de alteração do input de arquivo.
  *
  * @returns {Promise<void>} Promise resolvida após a tentativa de importação e atualização da listagem.
  */

  handleFileInputChange = async (event) => {

    this.obs.loading_import = true;

    const file = event.target.files[0];

    this.obs.file_name = file.name

    var data = new FormData();

    data.append("file", file);
    data.append("entrada", MvaStorePA.obs.selectedTab == "entrada" ? "true" : "false");
    data.append("estado", "pa");

    this.obs.open_import_produtos = false;

    let res = await MvaStorePA.handleImport(data)

    if (res) {

      MvaStorePA.obs.currentPage = 1;

      await MvaStorePA.handlePageFilter(1, "")

      this.obs.file_name = "Selecione um arquivo xlsx igual ao exportado!"

    }

    this.obs.loading_import = false;

  };

  render() {
    let items = MvaStorePA.obs.mvas?.map((mva) => {
      return (
        {
          id: mva.id,
          margem_distribuidor_12: mva.margem_distribuidor_12 ? mva.margem_distribuidor_12 + "%" : "",
          margem_distribuidor_19: mva.margem_distribuidor_19 ? mva.margem_distribuidor_19 + "%" : "",
          margem_distribuidor_4: mva.margem_distribuidor_4 ? mva.margem_distribuidor_4 + "%" : "",
          margem_distribuidor_7: mva.margem_distribuidor_7 ? mva.margem_distribuidor_7 + "%" : "",
          margem_industrial_12: mva.margem_industrial_12 ? mva.margem_industrial_12 + "%" : "",
          margem_industrial_19: mva.margem_industrial_19 ? mva.margem_industrial_19 + "%" : "",
          margem_industrial_4: mva.margem_industrial_4 ? mva.margem_industrial_4 + "%" : "",
          margem_industrial_7: mva.margem_industrial_7 ? mva.margem_industrial_7 + "%" : "",
          produto_cest: mva.produto_cest,
          produto_desc: mva.produto_desc,
          produto_ncm: mva.produto_ncm,
          item: mva.item,
          valido_de: mva.valido_de,
          valido_ate: mva.valido_ate,
          entrada: mva.entrada,
          seg_auto_pecas: mva.seg_auto_pecas,
          seg_vet_pet: mva.seg_vet_pet,
          seg_refrigerantes: mva.seg_refrigerantes,
          seg_agua_out: mva.seg_agua_out,
          seg_lamp_reatores: mva.seg_lamp_reatores,
          seg_mat_limpeza: mva.seg_mat_limpeza,
          seg_pneumaticos_prot: mva.seg_pneumaticos_prot,
          seg_alimenticios: mva.seg_alimenticios,
          seg_perf_cosm: mva.seg_perf_cosm,
          seg_eletro: mva.seg_eletro,
          seg_racoes: mva.seg_racoes,
          seg_sorvetes_fab: mva.seg_sorvetes_fab,
          seg_tintas: mva.seg_tintas,
          seg_mat_construcao: mva.seg_mat_construcao,
          seg_veiculos_automotores: mva.seg_veiculos_automotores,
          seg_veiculos_novos: mva.seg_veiculos_novos,
          seg_medicamentos_humano: mva.seg_medicamentos_humano,
        }
      );
    });

    const handleEditMva = (mva) => {
      this.obs.edit_mva = mva;

      if (this.obs.edit_mva.valido_de != null) {
        let newDate = new Date(this.obs.edit_mva.valido_de)
        newDate.setHours(newDate.getHours() + 24)
        this.obs.edit_mva.valido_de = newDate
      }

      if (this.obs.edit_mva.valido_ate != null) {
        let newDateAte = new Date(this.obs.edit_mva.valido_ate)
        newDateAte.setHours(newDateAte.getHours() - 3)
        this.obs.edit_mva.valido_ate = newDateAte
      }
    };

    const handleDuplicarMva = (mva) => {
      this.obs.duplicar_mva = mva;

      if (this.obs.duplicar_mva.valido_de != null) {
        let newDate = new Date(this.obs.duplicar_mva.valido_de)
        newDate.setHours(newDate.getHours() + 24)
        this.obs.duplicar_mva.valido_de = newDate
      }

      if (this.obs.duplicar_mva.valido_ate != null) {
        let newDateAte = new Date(this.obs.duplicar_mva.valido_ate)
        newDateAte.setHours(newDateAte.getHours() - 3)
        this.obs.duplicar_mva.valido_ate = newDateAte
      }
    }

    const buttonFormatterDetalhes = (cell, row) => {
      return (
        <>
          <MDBBtn
            color="default"
            rounded
            size="sm"
            onClick={(e) => handleEditMva(row)}
          >
            <img src="/images/edit.png" width={25} />
          </MDBBtn>
          <MDBBtn
            color="default"
            rounded
            size="sm"
            onClick={(e) => handleDuplicarMva(row)}
          >
            <img src="/images/duplicate.svg" width={25} />
          </MDBBtn>
          <Popover popoverClassName={Classes.POPOVER_CONTENT_SIZING}>
            <MDBBtn color="default" rounded size="sm">
              <img src="/images/bin.png" width={25} />
            </MDBBtn>
            <div>
              <H5>Confirmar exclusão?</H5>
              <p>Confirmar exclusão do MVA?</p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 15,
                }}
              >
                <Button
                  className={Classes.POPOVER_DISMISS}
                  style={{ marginRight: 10 }}
                >
                  Cancelar
                </Button>
                <Button
                  intent="danger"
                  className={Classes.POPOVER_DISMISS}
                  onClick={() => this.handleDeleteMva(row.id)}
                >
                  Excluir
                </Button>
              </div>
            </div>
          </Popover>
        </>
      );
    }

    const options = {
      searchField: this.createCustomSearchField,
    };

    let user_admin = AppStore.obs1.usuario ? AppStore.obs1.usuario.admin : false

    return (
      <div>

        <MDBRow>
          <MDBCol md="12">
            {user_admin &&
              <MDBBtn className="btnDefault noFloat" onClick={this.handleAdd} style={{ marginRight: 15 }}>
                Adicionar MVA
              </MDBBtn>
            }

            <Button
              icon="export"
              className="btnRoxo"
              onClick={() => this.handleExport()}
              loading={this.obs.loading_export}
              style={{ marginRight: 15 }}
            >
              Exportar
            </Button>

            {user_admin &&
              <Button
                icon="import"
                className="btnRoxo"
                onClick={() => this.handleImport()}
                loading={this.obs.loading_import}
              >
                Importar
              </Button>
            }

          </MDBCol>
        </MDBRow>

        <Tabs
          id="tabs"
          className={"tabsCalculos"}
          selectedTabId={MvaStorePA.obs.selectedTab}
          onChange={(tabId) => this.handleChangeTab(String(tabId))}
        >

          <Tab
            id="entrada"
            title="Entrada"
            panel={<>
              <MDBRow>
                <div style={{ display: "flex", width: "100%" }}>
                  <Switch
                    label={"Filtro Vigência"}
                    checked={MvaStorePA.obs.filter_vigencia}
                    onChange={(e: any) => this.handleChangeVigencia()}
                    innerLabelChecked="Vigentes" innerLabel="Não vigentes"
                    style={{ marginTop: 12, marginLeft: "15px", fontSize: 12, fontWeight: 600 }}
                    alignIndicator="right"
                  />
                </div>
              </MDBRow>

              <BootstrapTable
                className="TableDefault"
                ref="table"
                data={items}
                options={options}
                search
              >
                <TableHeaderColumn dataField="id" isKey={true} hidden width="1%">#</TableHeaderColumn>
                <TableHeaderColumn dataField="item" width="5%">Item</TableHeaderColumn>
                <TableHeaderColumn dataField="produto_cest" width="10%">CEST</TableHeaderColumn>
                <TableHeaderColumn dataField="produto_ncm" dataFormat={this.ncmFormatter} width="10%">NCM/SH</TableHeaderColumn>
                <TableHeaderColumn dataField="produto_desc" width="20%">Descrição</TableHeaderColumn>
                <TableHeaderColumn dataField="valido_de" dataFormat={this.validadeFormatter} width="15%">Valido de</TableHeaderColumn>
                <TableHeaderColumn dataField="valido_ate" dataFormat={this.validadeFormatter} width="15%">Valido até</TableHeaderColumn>
                <TableHeaderColumn dataField="margem_industrial_4" width="15%">Industrial 4%</TableHeaderColumn>
                <TableHeaderColumn dataField="margem_industrial_7" width="15%">Industrial 7%</TableHeaderColumn>
                <TableHeaderColumn dataField="margem_industrial_12" width="15%">Industrial 12%</TableHeaderColumn>
                <TableHeaderColumn dataField="margem_distribuidor_4" width="15%">Distribuidor 4%</TableHeaderColumn>
                <TableHeaderColumn dataField="margem_distribuidor_7" width="15%">Distribuidor 7%</TableHeaderColumn>
                <TableHeaderColumn dataField="margem_distribuidor_12" width="15%">Distribuidor 12%</TableHeaderColumn>
                {user_admin && <TableHeaderColumn dataFormat={buttonFormatterDetalhes} width="15%">Ações</TableHeaderColumn>}
              </BootstrapTable>

              <div style={{ marginTop: 5, float: "right", marginBottom: 120 }}>
                <Pager
                  currentPage={MvaStorePA.obs.currentPage}
                  totalCount={MvaStorePA.obs.length_mvas}
                  pageSize={20}
                  onPageChange={page => this.handlePageChange(MvaStorePA.obs.currentPage = page)}
                />
              </div>

            </>} />

          <Tab
            id="saida"
            title="Saída"
            panel={<>
              <MDBRow>
                <div style={{ display: "flex", width: "100%" }}>
                  <Switch
                    label={"Filtro Vigência"}
                    checked={MvaStorePA.obs.filter_vigencia}
                    onChange={(e: any) => this.handleChangeVigencia()}
                    innerLabelChecked="Vigentes" innerLabel="Não vigentes"
                    style={{ marginTop: 12, marginLeft: "15px", fontSize: 12, fontWeight: 600 }}
                    alignIndicator="right"
                  />
                </div>
              </MDBRow>

              <BootstrapTable
                className="TableDefault"
                ref="table"
                data={items}
                options={options}
                search
              >
                <TableHeaderColumn dataField="id" isKey={true} hidden width="1%">#</TableHeaderColumn>
                <TableHeaderColumn dataField="item" width="5%">Item</TableHeaderColumn>
                <TableHeaderColumn dataField="produto_cest" width="10%">CEST</TableHeaderColumn>
                <TableHeaderColumn dataField="produto_ncm" dataFormat={this.ncmFormatter} width="10%">NCM/SH</TableHeaderColumn>
                <TableHeaderColumn dataField="produto_desc" width="20%">Descrição</TableHeaderColumn>
                <TableHeaderColumn dataField="valido_de" dataFormat={this.validadeFormatter} width="15%">Valido de</TableHeaderColumn>
                <TableHeaderColumn dataField="valido_ate" dataFormat={this.validadeFormatter} width="15%">Valido até</TableHeaderColumn>
                {/* <TableHeaderColumn dataField="margem_industrial_4" width="15%">Industrial 4%</TableHeaderColumn> */}
                {/* <TableHeaderColumn dataField="margem_industrial_7" width="15%">Industrial 7%</TableHeaderColumn> */}
                <TableHeaderColumn dataField="margem_industrial_19" width="15%">Industrial 19%</TableHeaderColumn>
                {/* <TableHeaderColumn dataField="margem_distribuidor_4" width="15%">Distribuidor 4%</TableHeaderColumn> */}
                {/* <TableHeaderColumn dataField="margem_distribuidor_7" width="15%">Distribuidor 7%</TableHeaderColumn> */}
                <TableHeaderColumn dataField="margem_distribuidor_19" width="15%">Distribuidor 19%</TableHeaderColumn>
                {user_admin && <TableHeaderColumn dataFormat={buttonFormatterDetalhes} width="15%">Ações</TableHeaderColumn>}
              </BootstrapTable>

              <div style={{ marginTop: 5, float: "right", marginBottom: 120 }}>
                <Pager
                  currentPage={MvaStorePA.obs.currentPage}
                  totalCount={MvaStorePA.obs.length_mvas}
                  pageSize={20}
                  onPageChange={page => this.handlePageChange(MvaStorePA.obs.currentPage = page)}
                />
              </div>

            </>} />

        </Tabs>

        <Dialog
          isOpen={this.obs.open_import_produtos}
          title={"Importar MVAs - " + MvaStorePA.obs.selectedTab.toUpperCase()}
        >
          <div className={Classes.DIALOG_BODY}>

            <p style={{ color: "rgba(255, 0, 0, 0.82)" }}>

              <b>Observação:</b> Ao importar o arquivo, todos os mvas cadastrados no sistema serão substituídos pelos mvas presentes no arquivo, conforme o tipo selecionado na aba. <br />

            </p>

            <br />

            <FormGroup label="">
              <FileInput text={this.obs.file_name} className="bp3-fill bp3-file-input-has-selection input-file-certificado" onInputChange={this.handleFileInputChange} />
            </FormGroup>
          </div>

          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button text="Fechar" intent="primary" onClick={() => this.obs.open_import_produtos = false} />
            </div>
          </div>

        </Dialog>

        {this.obs.adding && <MvaPaDialogCreate onClose={this.handleAddClose} />}
        {this.obs.edit_mva && <MvaPaDialogEdit mva={this.obs.edit_mva} onClose={this.handleEditClose} />}
        {this.obs.duplicar_mva && <MvaPaDialogDuplicar mva={this.obs.duplicar_mva} onClose={this.handleDuplicaClose} />}
      </div >
    );
  }
}

export default MvasPaPage;
